import { bannedWordOr } from "@/lib/bannedWords";
import { optimizeImage, MEDIA_MAX_SIDE } from "@/lib/optimizeImage";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useVisibleDialogFrame } from "@/hooks/useVisibleDialogFrame";
import { Loader2, Hash, Upload } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import LinkedProductField from "@/components/video/LinkedProductField";
import { useSecuritySettings } from "@/hooks/useSecuritySettings";
import { normalizeTagName } from "@/lib/tagNames";
import { tagChipClassName } from "@/lib/tagChips";
import { useHashtagHolds } from "@/hooks/useHashtagHolds";

interface VideoEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoId: string;
  onSuccess: () => void;
}

const VideoEditDialog = ({ open, onOpenChange, videoId, onSuccess }: VideoEditDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [suggestedTags, setSuggestedTags] = useState<any[]>([]);
  const [tamaTags, setTamaTags] = useState<any[]>([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [currentThumbnail, setCurrentThumbnail] = useState<string>("");
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [userProducts, setUserProducts] = useState<any[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  // Champ produit réservé au propriétaire de la vidéo ayant le rôle pro/admin
  const [canLinkProduct, setCanLinkProduct] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  /** Compte connecté, pour ses hashtags d'activité. */
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const { holds: myHolds, canBuy: canBuyHashtags } = useHashtagHolds(authUserId);
  const myHoldNames = new Set(myHolds.map((hold) => normalizeTagName(hold.tag_name)));
  const visibleDialogFrame = useVisibleDialogFrame(open);
  const { data: securitySettings } = useSecuritySettings();
  const minTags = securitySettings?.rate_limiting?.video_tags_min || 1;
  const maxTags = securitySettings?.rate_limiting?.video_tags_max || 5;

  useEffect(() => {
    if (open && videoId) {
      fetchVideoData();
      fetchSuggestedTags();
    }
  }, [open, videoId]);

  useEffect(() => {
    if (userId) {
      fetchUserProducts();
      checkProductLinkRole();
    }
  }, [userId]);

  const checkProductLinkRole = async () => {
    const { data: authData } = await supabase.auth.getUser();
    const currentUserId = authData.user?.id;

    setAuthUserId(currentUserId ?? null);

    if (!currentUserId) {
      setIsAdmin(false);
      setCanLinkProduct(false);
      return;
    }

    const [{ data: isPro }, { data: isAdmin }] = await Promise.all([
      supabase.rpc("has_role", { _user_id: currentUserId, _role: "pro" }),
      supabase.rpc("has_role", { _user_id: currentUserId, _role: "admin" }),
    ]);

    setIsAdmin(Boolean(isAdmin));
    setCanLinkProduct(currentUserId === userId && (Boolean(isPro) || Boolean(isAdmin)));
  };


  const fetchVideoData = async () => {
    // Fetch video info
    const { data: videoData } = await supabase
      .from("videos")
      .select("title, description, thumbnail_url, product_id, user_id")
      .eq("id", videoId)
      .single();

    if (videoData) {
      setTitle(videoData.title || "");
      setDescription(videoData.description || "");
      setCurrentThumbnail(videoData.thumbnail_url || "");
      setSelectedProductId(videoData.product_id);
      setUserId(videoData.user_id);
    }

    // Fetch video tags
    const { data: videoTags } = await supabase
      .from("video_tags")
      .select(`
        tags:tag_id (
          id,
          name,
          is_reserved
        )
      `)
      .eq("video_id", videoId);

    if (videoTags) {
      const tags = videoTags.map((vt: any) => vt.tags.name);
      setSelectedTags(tags);
    }

    // Reset thumbnail preview
    setThumbnailPreview(null);
    setThumbnailFile(null);
  };

  const handleThumbnailSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier le format
    const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedFormats.includes(file.type)) {
      toast({
        variant: "destructive",
        title: "Format invalide",
        description: "Formats acceptés : JPG, PNG, WEBP"
      });
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "Fichier trop volumineux",
        description: "La taille maximale est de 5 MB"
      });
      return;
    }

    // Vérifier la résolution
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      
      // Vérification des dimensions minimales
      if (width < 640) {
        toast({
          variant: "destructive",
          title: "Largeur insuffisante",
          description: `Votre image fait ${width}px de large. Minimum requis : 640px`
        });
        URL.revokeObjectURL(objectUrl);
        return;
      }
      if (height < 360) {
        toast({
          variant: "destructive",
          title: "Hauteur insuffisante",
          description: `Votre image fait ${height}px de haut. Minimum requis : 360px`
        });
        URL.revokeObjectURL(objectUrl);
        return;
      }

      // Vérification des dimensions maximales
      if (width > 5760) {
        toast({
          variant: "destructive",
          title: "Largeur trop élevée",
          description: `Votre image fait ${width}px de large. Maximum autorisé : 5760px`
        });
        URL.revokeObjectURL(objectUrl);
        return;
      }
      if (height > 3240) {
        toast({
          variant: "destructive",
          title: "Hauteur trop élevée",
          description: `Votre image fait ${height}px de haut. Maximum autorisé : 3240px`
        });
        URL.revokeObjectURL(objectUrl);
        return;
      }

      setThumbnailFile(file);
      setThumbnailPreview(objectUrl);
    };

    img.onerror = () => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de lire l'image"
      });
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };

  const fetchSuggestedTags = async () => {
    const [{ data }, { data: reservedData }] = await Promise.all([
      (supabase as any)
        .from("tags")
        .select("id, name, usage_count, is_reserved, is_held")
        .order("usage_count", { ascending: false })
        .limit(50),
      (supabase as any)
        .from("tags")
        .select("id, name, usage_count, is_reserved, is_held")
        .eq("is_reserved", true)
        .order("name", { ascending: true }),
    ]);

    if (data) {
      setSuggestedTags(data);
    }
    if (reservedData) {
      setTamaTags(reservedData);
    }
  };

  const fetchUserProducts = async () => {
    if (!userId) return;

    const { data } = await supabase
      .from("products")
      .select("id, title, type, thumbnail_url")
      .eq("user_id", userId)
      .eq("is_active", true)
      .eq("is_tama_catalog", false)
      .order("created_at", { ascending: false });

    if (data) {
      setUserProducts(data);
    }
  };

  const isKnownReservedTag = (tagName: string) => {
    const normalizedTag = normalizeTagName(tagName);
    return [...suggestedTags, ...tamaTags].some(
      (tag) => normalizeTagName(tag.name) === normalizedTag && tag.is_reserved,
    );
  };

  /** Hashtag tenu par un compte professionnel (fond carte, pas le rouge TAMA). */
  const isKnownHeldTag = (tagName: string) => {
    const normalizedTag = normalizeTagName(tagName);
    if (myHoldNames.has(normalizedTag)) return true;
    return [...suggestedTags, ...tamaTags].some(
      (tag) => normalizeTagName(tag.name) === normalizedTag && tag.is_held,
    );
  };

  const handleAddTag = async (tagName: string) => {
    const normalizedTag = normalizeTagName(tagName);
    if (!normalizedTag || selectedTags.includes(normalizedTag)) return;

    let isReserved = isKnownReservedTag(normalizedTag);
    let isHeldByOther = !myHoldNames.has(normalizedTag) && isKnownHeldTag(normalizedTag);
    if (!isReserved && !isHeldByOther) {
      const { data: existingTag } = await (supabase as any)
        .from("tags")
        .select("is_reserved, is_held")
        .eq("name", normalizedTag)
        .maybeSingle();
      isReserved = Boolean((existingTag as any)?.is_reserved);
      isHeldByOther =
        Boolean((existingTag as any)?.is_held) && !myHoldNames.has(normalizedTag);
    }

    if (isReserved && !isAdmin) {
      toast({
        variant: "destructive",
        title: "Hashtag réservé",
        description: "Ce hashtag est reserve a TAMA.",
      });
      setTagInput("");
      setShowTagSuggestions(false);
      return;
    }

    if (isHeldByOther && !isAdmin) {
      toast({
        variant: "destructive",
        title: "Hashtag réservé",
        description: "Ce hashtag est déjà réservé.",
      });
      setTagInput("");
      setShowTagSuggestions(false);
      return;
    }
    
    
    if (selectedTags.length >= maxTags) {
      toast({
        variant: "destructive",
        title: "Maximum atteint",
        description: `Vous ne pouvez ajouter que ${maxTags} tags maximum`,
      });
      return;
    }

    setSelectedTags([...selectedTags, normalizedTag]);
    setTagInput("");
    setShowTagSuggestions(false);
  };

  const handleRemoveTag = (tagToRemove: string) => {
    if (selectedTags.length <= minTags) {
      toast({
        variant: "destructive",
        title: "Hashtag requis",
        description:
          minTags > 1
            ? `Une video doit avoir au moins ${minTags} hashtags.`
            : "Une video doit avoir au moins un hashtag.",
      });
      return;
    }
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTagInput(value);
    setShowTagSuggestions(value.length > 0);
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag(tagInput);
    }
  };

  const availableTamaTags = tamaTags.filter(
    (tag) => !selectedTags.includes(normalizeTagName(tag.name)),
  );

  /** Hashtags d'activité tenus par le compte, encore à poser sur cette vidéo. */
  const availableActivityTags = myHolds.filter(
    (hold) => !selectedTags.includes(normalizeTagName(hold.tag_name)),
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTags.length < minTags) {
      toast({
        variant: "destructive",
        title: "Hashtag requis",
        description:
          minTags > 1
            ? `Veuillez ajouter au moins ${minTags} hashtags à votre vidéo.`
            : "Veuillez ajouter au moins un hashtag à votre vidéo.",
      });
      return;
    }
    setLoading(true);

    try {
      let thumbnailUrl = currentThumbnail;

      // Upload new thumbnail if provided
      if (thumbnailFile) {
        const optimized = await optimizeImage(thumbnailFile, { maxSide: MEDIA_MAX_SIDE, baseName: "thumb" });
        const fileExt = optimized.name.split(".").pop();
        const fileName = `${videoId}-${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("thumbnails")
          .upload(fileName, optimized, { contentType: optimized.type });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from("thumbnails")
          .getPublicUrl(fileName);

        thumbnailUrl = publicUrl;
      }

      // Update video info
      const { error: updateError } = await supabase
        .from("videos")
        .update({
          title: title.trim() || null,
          description: description.trim() || null,
          thumbnail_url: thumbnailUrl,
          product_id: selectedProductId,
        })
        .eq("id", videoId);

      if (updateError) throw updateError;

      // Synchronise la sélection sans réinsérer les hashtags déjà posés.
      const { data: currentVideoTags } = await supabase
        .from("video_tags")
        .select("tag_id, tags:tag_id(name, is_reserved)")
        .eq("video_id", videoId);
      const selectedTagSet = new Set(selectedTags.map((tag) => normalizeTagName(tag)));
      const existingTagNames = new Set(
        (currentVideoTags || [])
          .map((row: any) => normalizeTagName(row.tags?.name || ""))
          .filter(Boolean),
      );
      const tagIdsToDelete = (currentVideoTags || [])
        .filter((row: any) => !selectedTagSet.has(normalizeTagName(row.tags?.name || "")))
        .map((row) => row.tag_id);

      if (tagIdsToDelete.length > 0) {
        const { error: deleteTagsError } = await supabase
          .from("video_tags")
          .delete()
          .eq("video_id", videoId)
          .in("tag_id", tagIdsToDelete);

        if (deleteTagsError) {
          toast({
            variant: "destructive",
            title: "Hashtags non modifiés",
            description: "Certains hashtags n'ont pas pu être retirés.",
          });
        }
      }

      // Add new tags
      if (selectedTags.length > 0) {
        for (const tagName of selectedTags) {
          if (existingTagNames.has(normalizeTagName(tagName))) {
            continue;
          }

          // Check if tag exists, create if not
          let { data: existingTag } = await (supabase as any)
            .from("tags")
            .select("id, is_reserved, is_held")
            .eq("name", tagName)
            .maybeSingle();

          if (existingTag?.is_reserved && !isAdmin) {
            continue;
          }

          if (
            (existingTag as any)?.is_held &&
            !isAdmin &&
            !myHoldNames.has(normalizeTagName(tagName))
          ) {
            toast({
              variant: "destructive",
              title: "Hashtag non ajouté",
              description: "Ce hashtag est déjà réservé.",
            });
            continue;
          }

          let tagId = existingTag?.id;

          if (!tagId) {
            const { data: newTag, error: tagError } = await (supabase as any)
              .from("tags")
              .insert({ name: tagName })
              .select("id")
              .single();
            if (tagError) {
              toast({
                variant: "destructive",
                title: "Hashtag non ajouté",
                description: `#${tagName} n'a pas pu être enregistré.`,
              });
              continue;
            }
            tagId = newTag?.id;
          }

          if (tagId) {
            const { error: videoTagError } = await supabase
              .from("video_tags")
              .insert({
                video_id: videoId,
                tag_id: tagId,
              });
            if (videoTagError) {
              toast({
                variant: "destructive",
                title: "Hashtag non ajouté",
                description: `#${tagName} n'a pas pu être lié à la vidéo.`,
              });
            }
          }
        }
      }

      toast({
        title: "Vidéo modifiée",
        description: "Les modifications ont été enregistrées",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: bannedWordOr(error, error.message || "Impossible de modifier la vidéo"),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="dialog-ios-visible-frame flex max-w-md max-h-[min(90dvh,calc(100svh-var(--player-bottom-inset)-1rem))] flex-col gap-0 overflow-hidden p-0"
        style={visibleDialogFrame}
      >
        <DialogHeader className="flex-shrink-0 p-6 pb-4">
          <DialogTitle>Modifier la vidéo</DialogTitle>
          <DialogDescription>
            Modifiez les informations de votre vidéo
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 space-y-4">
            <div className="space-y-2">
            <Label htmlFor="title">Titre <span className="text-destructive">*</span></Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre de la vidéo"
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description de la vidéo"
              rows={3}
              maxLength={500}
            />
          </div>

          <div className="space-y-2">
            <Label>Miniature</Label>
            {(thumbnailPreview || currentThumbnail) && (
              <div className="relative mx-auto w-32 aspect-[9/16] bg-secondary rounded-lg overflow-hidden mb-2">
                <img 
                  src={thumbnailPreview || currentThumbnail} 
                  alt="Miniature" 
                  className="w-full h-full object-cover" 
                />
              </div>
            )}
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleThumbnailSelect}
              className="hidden"
              id="thumbnail-edit"
            />
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => document.getElementById('thumbnail-edit')?.click()}
            >
              <Upload className="mr-2 h-4 w-4" />
              {thumbnailPreview || currentThumbnail ? "Changer la miniature" : "Ajouter une miniature"}
            </Button>
            <p className="text-xs text-muted-foreground">
              Formats : JPG, PNG, WEBP • Taille max : 5 MB<br />
              Résolution recommandée : 720x1280 à 1440x2560 pixels
            </p>
          </div>

          <LinkedProductField
            canLinkProduct={canLinkProduct}
            products={userProducts}
            selectedProductId={selectedProductId}
            onChange={setSelectedProductId}
            onNavigateAway={() => onOpenChange(false)}
          />


          <div className="space-y-2">
            <Label htmlFor="tags">Tags (min {minTags}, max {maxTags})</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedTags.map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  role="button"
                  tabIndex={0}
                  aria-label={`Retirer le hashtag ${tag}`}
                  className={tagChipClassName({ name: tag, is_reserved: isKnownReservedTag(tag), is_held: isKnownHeldTag(tag) }, "gap-1 cursor-pointer select-none")}
                  onClick={() => handleRemoveTag(tag)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleRemoveTag(tag);
                    }
                  }}
                >
                  <Hash className="h-3 w-3" />
                  {tag}
                </Badge>
              ))}
            </div>
            <div className="relative">
              <Input
                id="tags"
                placeholder="Ajouter un tag..."
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagInputKeyDown}
                disabled={selectedTags.length >= maxTags}
                maxLength={30}
              />
              {showTagSuggestions && suggestedTags.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-40 overflow-y-auto">
                  {suggestedTags
                    .filter(tag => 
                      (isAdmin || !tag.is_reserved) &&
                      (isAdmin || !tag.is_held || myHoldNames.has(normalizeTagName(tag.name))) &&
                      normalizeTagName(tag.name).includes(normalizeTagName(tagInput)) &&
                      !selectedTags.includes(normalizeTagName(tag.name))
                    )
                    .slice(0, 5)
                    .map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => handleAddTag(tag.name)}
                        className="w-full px-3 py-2 text-left hover:bg-accent flex items-center justify-between"
                      >
                        <span className="flex items-center gap-2">
                          <Hash className="h-4 w-4" />
                          {tag.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {tag.usage_count}
                        </span>
                      </button>
                    ))}
                </div>
              )}
            </div>
            {suggestedTags.length > 0 && selectedTags.length < maxTags && (
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs text-muted-foreground">Suggestions :</span>
                {suggestedTags
                  .filter(tag => !tag.is_reserved && (!tag.is_held || myHoldNames.has(normalizeTagName(tag.name))) && !selectedTags.includes(normalizeTagName(tag.name)))
                  .slice(0, 5)
                  .map((tag) => (
                    <Badge
                      key={tag.id}
                      variant="outline"
                      className="cursor-pointer hover:bg-accent"
                      onClick={() => handleAddTag(tag.name)}
                    >
                      <Hash className="h-3 w-3 mr-1" />
                      {tag.name}
                    </Badge>
                  ))}
              </div>
            )}
            {isAdmin && (
              <div className="space-y-2">
                <Label htmlFor="edit-tama-tags-select">Ajouter un hashtag TAMA</Label>
                <Select
                  key={`edit-tama-tags-${selectedTags.join("-")}`}
                  disabled={availableTamaTags.length === 0 || selectedTags.length >= maxTags}
                  onValueChange={(value) => void handleAddTag(value)}
                >
                  <SelectTrigger id="edit-tama-tags-select">
                    <SelectValue
                      placeholder={
                        availableTamaTags.length > 0
                          ? "Ajouter un hashtag TAMA"
                          : "Tous les hashtags TAMA sont ajoutés"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTamaTags.map((tag) => (
                      <SelectItem key={tag.id} value={normalizeTagName(tag.name)}>
                        #{tag.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Visible de tous, reserve a TAMA.</p>
              </div>
            )}
            {canBuyHashtags && myHolds.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="edit-activity-tags-select">Ajouter un hashtag d'activité</Label>
                <Select
                  key={`edit-activity-tags-${selectedTags.join("-")}`}
                  disabled={availableActivityTags.length === 0 || selectedTags.length >= maxTags}
                  onValueChange={(value) => void handleAddTag(value)}
                >
                  <SelectTrigger id="edit-activity-tags-select">
                    <SelectValue
                      placeholder={
                        availableActivityTags.length > 0
                          ? "Ajouter un hashtag d'activité"
                          : "Tous vos hashtags d'activité sont ajoutés"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableActivityTags.map((hold) => (
                      <SelectItem key={hold.id} value={normalizeTagName(hold.tag_name)}>
                        #{hold.tag_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Visible de tous, réservé à votre activité.
                </p>
              </div>
            )}
          </div>

          </div>

          <div className="flex flex-shrink-0 gap-2 justify-end border-t border-border bg-background p-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enregistrer
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default VideoEditDialog;
